/**
 * 首页 —— 模块卡片网格 + 快捷搜索入口 + 收藏/最近。
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { modules, moduleTint, type EncyclopediaModule, type ModuleTint } from '@/core/moduleCatalog';
import type { ContentRepository } from '@/core/contentRepository';
import type { EncyclopediaEntry } from '@/core/models';
import { EntryIcon } from '@/components/EntryIcon';
import { resolveSymbol } from '@/lib/icons';
import { cn } from '@/lib/cn';
import { useLibraryStore } from '@/store/useLibraryStore';

interface HomePageProps {
  repository: ContentRepository;
}

const quickPreviewGroups = [
  {
    title: '人物',
    topicID: 'social.villagers',
    preferredEntryIDs: ['character.abigail', 'character.robin', 'character.marnie', 'character.clint'],
  },
  {
    title: '种子/作物',
    topicID: 'farming.crops',
    preferredEntryIDs: ['crop.parsnip', 'crop.potato', 'crop.strawberry', 'crop.blueberry'],
  },
  {
    title: '加工设备',
    topicID: 'farming.artisan',
    preferredEntryIDs: ['artisan.keg', 'artisan.preserves-jar', 'artisan.mayonnaise-machine', 'artisan.loom'],
  },
  {
    title: '工具/铁匠',
    topicID: 'items.tools',
    preferredEntryIDs: ['tool.pickaxe', 'tool.axe', 'tool.hoe', 'tool.watering-can'],
  },
  {
    title: '建筑/木匠',
    topicID: 'farming.buildings',
    preferredEntryIDs: ['building.coop', 'building.barn', 'building.stable', 'building.mill'],
  },
];

function isEntry(entry: EncyclopediaEntry | undefined): entry is EncyclopediaEntry {
  return Boolean(entry);
}

export function HomePage({ repository }: HomePageProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const favoriteIDs = useLibraryStore((s) => s.favoriteIDs);
  const recentVisits = useLibraryStore((s) => s.recentVisits);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (q) navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const favoriteEntries = favoriteIDs
    .map((id) => repository.entry(id))
    .filter(isEntry)
    .slice(0, 8);

  const recentEntries = recentVisits
    .map((v) => repository.entry(v.entryID))
    .filter(isEntry)
    .slice(0, 8);

  const quickGroups = quickPreviewGroups
    .map((group) => ({
      ...group,
      entries: orderedPreviewEntries(
        group.preferredEntryIDs,
        repository.entriesForTopic(group.topicID),
      ),
    }))
    .filter((group) => group.entries.length > 0);

  return (
    <div className="space-y-8">
      {/* 搜索栏 */}
      <form onSubmit={handleSearch} className="relative">
        <Search
          size={18}
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="搜索条目（名称、别名、说明…）"
          className="w-full rounded-2xl border border-slate-200 bg-surface-light py-3.5 pl-12 pr-4 text-base shadow-sm outline-none transition-colors placeholder:text-slate-400 focus:border-sage focus:ring-2 focus:ring-sage/20 dark:border-slate-700 dark:bg-surface-dark dark:focus:border-sage dark:focus:ring-sage/30"
        />
      </form>

      {/* 收藏快捷入口 */}
      {favoriteEntries.length > 0 && (
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
              收藏
            </h2>
            <Link to="/favorites" className="text-xs text-sage hover:underline">
              查看全部
            </Link>
          </div>
          <div className="flex flex-wrap gap-2">
            {favoriteEntries.map((entry) => (
              <Link
                key={entry.id}
                to={`/topic/${entry.topicID}#${entry.id}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-sage/10 px-3 py-1.5 text-xs text-sage transition-colors hover:bg-sage/20"
              >
                {entry.nameZh}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* 最近浏览快捷入口 */}
      {recentEntries.length > 0 && (
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
              最近浏览
            </h2>
            <Link to="/recent" className="text-xs text-sage hover:underline">
              查看全部
            </Link>
          </div>
          <div className="flex flex-wrap gap-2">
            {recentEntries.map((entry) => (
              <Link
                key={entry.id}
                to={`/topic/${entry.topicID}#${entry.id}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-xs text-slate-600 transition-colors hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700"
              >
                {entry.nameZh}
              </Link>
            ))}
          </div>
        </section>
      )}

      {quickGroups.length > 0 && (
        <section>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-400">
            常用速查
          </h2>
          <div className="space-y-3">
            {quickGroups.map((group) => (
              <QuickPreviewCard
                key={group.title}
                title={group.title}
                entries={group.entries}
              />
            ))}
          </div>
        </section>
      )}

      {/* 模块卡片网格 */}
      <section>
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-400">
          百科模块
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {modules.map((mod) => (
            <ModuleCard key={mod.id} mod={mod} />
          ))}
        </div>
      </section>
    </div>
  );
}

function orderedPreviewEntries(
  preferredEntryIDs: readonly string[],
  topicEntries: readonly EncyclopediaEntry[],
) {
  const entriesByID = new Map(topicEntries.map((entry) => [entry.id, entry]));
  const preferred = preferredEntryIDs
    .map((id) => entriesByID.get(id))
    .filter(isEntry);
  const preferredIDs = new Set(preferred.map((entry) => entry.id));
  const remaining = topicEntries
    .filter((entry) => !preferredIDs.has(entry.id))
    .sort((a, b) => a.nameZh.localeCompare(b.nameZh, 'zh-Hans-CN'));

  return [...preferred, ...remaining];
}

function QuickPreviewCard({
  title,
  entries,
}: {
  title: string;
  entries: readonly EncyclopediaEntry[];
}) {
  const [expanded, setExpanded] = useState(false);
  const canToggle = entries.length > 4;

  return (
    <div className="rounded-2xl border border-slate-200/60 bg-surface-light p-4 shadow-sm dark:border-slate-700/40 dark:bg-surface-dark">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{title}</h3>
        {canToggle && (
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={`${expanded ? '收起' : '查看更多'} ${title}`}
            onClick={() => setExpanded((value) => !value)}
            className="text-xs font-medium text-sage hover:underline"
          >
            {expanded ? '收起' : '查看更多'}
          </button>
        )}
      </div>
      <div
        aria-label={`${title} 常用条目`}
        className={cn(
          'grid grid-cols-[repeat(auto-fill,minmax(5.25rem,7rem))] justify-start gap-2 transition-[max-height] duration-200',
          canToggle && !expanded ? 'max-h-[5.5rem] overflow-hidden' : 'max-h-none',
        )}
      >
        {entries.map((entry) => (
          <Link
            key={entry.id}
            to={`/topic/${entry.topicID}#${entry.id}`}
            className="flex min-h-10 min-w-0 items-center gap-1.5 rounded-xl bg-slate-50 px-2 py-1.5 transition-colors hover:bg-sage/10 dark:bg-slate-800/70 dark:hover:bg-sage/20"
          >
            <EntryIcon entry={entry} size={24} />
            <span className="truncate text-xs font-medium text-slate-700 dark:text-slate-200">
              {entry.nameZh}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------- ModuleCard ----------------------------- */

const moduleCardColors: Record<ModuleTint, { bg: string; text: string; hover: string }> = {
  ocean: { bg: 'bg-ocean/10', text: 'text-ocean', hover: 'hover:bg-ocean/15' },
  teal: { bg: 'bg-teal-500/10', text: 'text-teal-600', hover: 'hover:bg-teal-500/15' },
  indigo: { bg: 'bg-indigo-500/10', text: 'text-indigo-600', hover: 'hover:bg-indigo-500/15' },
  sage: { bg: 'bg-sage/10', text: 'text-sage', hover: 'hover:bg-sage/15' },
  plum: { bg: 'bg-plum/10', text: 'text-plum', hover: 'hover:bg-plum/15' },
  amber: { bg: 'bg-amber/10', text: 'text-amber', hover: 'hover:bg-amber/15' },
  orange: { bg: 'bg-orange-500/10', text: 'text-orange-600', hover: 'hover:bg-orange-500/15' },
  brown: { bg: 'bg-amber-700/10', text: 'text-amber-800', hover: 'hover:bg-amber-700/15' },
};

function ModuleCard({ mod }: { mod: EncyclopediaModule }) {
  const tint = moduleTint[mod.id] ?? 'sage';
  const colors = moduleCardColors[tint];
  const Icon = resolveSymbol(mod.symbolName);

  return (
    <Link
      to={`/module/${mod.id}`}
      className={cn(
        'group flex items-center gap-4 rounded-2xl border border-slate-200/60 p-4 shadow-sm transition-all',
        colors.bg,
        colors.hover,
        'dark:border-slate-700/40',
      )}
    >
      <div className={cn('flex shrink-0 items-center justify-center rounded-xl', colors.bg)}>
        <Icon className={cn('p-2', colors.text)} size={48} strokeWidth={1.5} />
      </div>
      <div className="min-w-0">
        <h3 className={cn('text-base font-semibold', colors.text)}>{mod.title}</h3>
        <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
          {mod.summary}
        </p>
      </div>
    </Link>
  );
}
